<template>
  <section-page title="Imports">
    <div class="import-page-container columns">
      <div class="column is-5">
        <h4 class="import-heading">Imports Instructions</h4>
        <app-instruction number="1">There are two types of imports "Leads with Contact" and "Contacts Only"</app-instruction>
        <app-instruction number="2">Adjust your CSV file so it conforms to Forefront specs. <a :href="sampleLink">Download Sample CSV</a>.</app-instruction>
        <app-instruction number="3">Upload your CSV file for validation</app-instruction>
        <app-instruction number="4">If there are no errors or warnings you can immediately import the leads</app-instruction>
        <app-instruction number="5">If not you’ll get a validation report that will help you adjust the CSV file</app-instruction>
        <app-instruction number="6">Then re-upload the adjusted CSV file and revalidate</app-instruction>
        <app-instruction number="7">After you complete your import you’ll have one week to rollback your import</app-instruction>

        <h4 class="import-heading mt-200">Good To Know</h4>
        <app-instruction number="1">You can use <a :href="sampleLink">Sample CSV</a> for both importing "Leads with Contact" or "Contacts Only".</app-instruction>
        <app-instruction number="2">Make sure your old status names match exactly with the status names in Forefront.</app-instruction>
        <app-instruction number="3">If you import a lead without a matching status we will use whatever default status you've selected.</app-instruction>
        <app-instruction number="4">Make sure your contact types and tags are already added in <a href="/settings/contacts">Contact Settings</a> page and it should match exactly.</app-instruction>
        <app-instruction number="5">Due to the complex nature of follow-up sequences, we can’t import leads directly so instead, we recommend adding a temporary status labeled “Follow Up” that you import all the leads you want in a follow-up sequence. Then you will have to manually go add the desired leads to the appropriate follow-up sequence.</app-instruction>
      </div>

      <div class="column is-7">
        <h4 class="import-heading">Past / Current Imports</h4>

        <app-loading-section :loading="isLoading">
          <div v-show="!file" class="uploader">
            <b-upload v-model="file" drag-drop>
              <div class="upload-inner">
                <icon-default
                  icon="import"
                  size="is-large"
                  color="is-light"
                ></icon-default>
                <p class="mt-50">Drop your files here or click to upload</p>
              </div>
            </b-upload>
          </div>

          <div v-if="file" class="upload-form">
            <form-field-input
              label="File"
              v-model="file.name"
              :validation-errors="validationErrors.name"
              disabled=""
            ></form-field-input>

            <form-field-select
              label="Type"
              v-model="type"
              :validation-errors="validationErrors.type"
              help-text="This indicate which one are you going to import"
            >
              <option :value="ENUMS.ImportTypeEnum.Record.value">Leads with Contact</option>
              <option :value="ENUMS.ImportTypeEnum.Contact.value">Contacts Only</option>
            </form-field-select>

            <form-field-input
              label="Label"
              v-model="label"
              :validation-errors="validationErrors.label"
              v-autofocus
            ></form-field-input>

            <form-field-select-status
              v-if="this.type === ENUMS.ImportTypeEnum.Record.value"
              label="Default Status"
              v-model="status_id"
              include-no-status
              :validation-errors="validationErrors.status_id"
              help-text="Status to place imported Leads without a matching status"
            ></form-field-select-status>

            <div class="flex flex-vertical-center flex-between mt-100">
              <button-link
                @click="close"
              >Cancel Upload</button-link>

              <button-default
                @click="create"
                :loading="isUploading"
                :disabled="isUploading"
              >Start Validation</button-default>
            </div>
          </div>

          <import-item
            v-for="port in imports"
            :key="port.id"
            :port="port"
          ></import-item>
        </app-loading-section>
      </div>
    </div>
  </section-page>
</template>

<script>
import ImportItem from './components/ImportItem';
import { subscribeToChannel, unsubscribeToChannel } from '../../services/pusher';
import FormValidationErrors from "../../components/form/FormValidationErrors";
import {ENUMS} from "../../services/enums";

export default {
  name: "ImportTab",
  components: {ImportItem },
  data() {
    return {
      isLoading: true,
      isUploading: false,
      file: null,
      label: null,
      status_id: null,
      type: ENUMS.ImportTypeEnum.Record.value,
      openedDetails: [],
      validationErrors: {}
    }
  },
  computed: {
    sampleLink() {
      return `${process.env.MIX_BASE_API_URL}v1/imports/download_sample?account_id=${this.$store.getters.account.id}`
    },
    imports() {
      const copy = JSON.parse(JSON.stringify(this.$store.state.imports));
      return copy.sort((a,b) => new Date(b.created_at) - new Date(a.created_at));
    },
  },
  beforeRouteLeave (to, from, next) {
    if (this.$store.state.isLoggedIn) {
      unsubscribeToChannel('imports', this.$store.getters.account.id);
    }
    next();
  },
  created() {
    this.get();
  },
  methods: {
    get() {
      this.isLoading = true;
      this.$api.imports.all().then((res) => {
        this.$store.commit('setImports', res.data);
        this.isLoading = false;
        subscribeToChannel('imports', this.$store.getters.account.id);
      });
    },
    create() {
      const formData = new FormData();
      this.isUploading = true;
      // make sure status_id is null when importing contacts only
      this.status_id = (this.type === ENUMS.ImportTypeEnum.Record.value) ? this.status_id : null

      formData.append('file', this.file);
      formData.append('label', this.label);
      formData.append('status_id', this.status_id);
      formData.append('type', this.type)
      formData.append('file_name', this.file.name);
      formData.append('market_id', this.$store.getters.market.id);

      this.getRowCount().then(count => {
        if (count > 1000) {
          this.$buefy.snackbar.open({
            message:
              'Cannot Import csv file with more than 1000 records, please contact support if you need to import a large amount of records',
            type: 'is-danger',
            indefinite: true
          });

          this.close();
          return;
        }

        formData.append('total_record_count', count);

        this.$api.imports.create(formData).then(() => {
          this.$buefy.toast.open({
            message: 'Import was created',
            type: 'is-success'
          });

          this.close();
        }, error => {
          this.isUploading = false;
          if (error.response.status === 422) this.validationErrors = error.response.data.errors;
        });
      });
    },
    close() {
      this.isUploading = false;
      this.file = null;
      this.label = null;
    },
    getRowCount() {
      return new Promise((resolve) => {
        const reader = new FileReader();

        reader.onloadend = (evt) => {
          if (evt.target.readyState === FileReader.DONE) {
            let rows = evt.target.result.match(/("([^"]|"")*"|[^,\n]*)(,("([^"]|"")*"|[^,\n]*))*\n/g);
            let removedEmptyRowsArr = rows.filter(row => row.trim().replace(/"|,/g, '') !== '');

            resolve(removedEmptyRowsArr.length - 1);
          }
        }

        reader.readAsBinaryString(this.file);
      });
    }
  }
}
</script>

<style lang="scss">
@import "../../styles/variables/variables";

.import-heading {
  margin-bottom: 1rem;
  font-size: 1.25rem;
  font-weight: bold;
}

.uploader {
  position: relative;
  width: 100%;
  height: 120px;
  margin-top: 10px;
  text-align: center;

  .upload {
    width: 100%;
    height: 100%;
  }

  .upload-draggable {
    width: 100%;

    .upload-inner {
      font-size: 1.1rem;
      position: relative;
      top: 50%;
      transform: translate(0, -50%);
    }
  }
}

.upload-form {
  padding: 20px;
  border: 1px solid $border;
}
</style>
